/* eslint-disable react/prop-types */
/* eslint-disable no-underscore-dangle */
import React from 'react';

import { Grid, Breadcrumb, Button } from 'semantic-ui-react';

import { graphql } from 'gatsby';
import _isEmpty from 'lodash/isEmpty';
import _isNil from 'lodash/isNil';

import {
  ContactForm,
  InternalLink,
  Location,
  Map,
  SEO,
  BreadcrumbListSchema,
  LocationSchema,
  Email,
  Phone,
  HoursOfOperation,
  MultipleComponentIterator,
  HoverContainer,
  FishermanIcon,
  TeamMemberV2,
  Image,
  SlideReveal,
  PaginatedGrid,
  multipleComponentIterator,
  EVENT_CATEGORIES,
  EVENT_ACTIONS,
  EVENT_LABELS,
  WebsiteComponentInteraction,
  FishermanHeader,
  MarkdownContent,
} from '@bluefin/components';

import LocationServices from '../../components/LocationServices/LocationServices';
import { createLocationSlug, createTeamMemberSlug } from '../../index';
import {
  createLocationName,
  getBusinessFile,
  getBusinessFiles,
  getComponentContentNodeContent,
  getLocationTeamMembers,
  getPageByType,
  getRelatedServicesForLocation,
} from '../../template-utils';
import {
  constructFullPageUrl,
  constructFullPageUrlFromSegments,
  ensureSlugSlashes,
  getPageUrl,
} from '../../utils';

export default function LocationTemplate({ data, pageContext }) {
  const {
    fishermanBusinessLocation: location = {},
    allFishermanBusinessLocation: { nodes: allLocations = [] },
    fishermanBusiness: business = {},
    site: {
      siteMetadata: { baseUrl },
    },
    allFishermanBusinessTeam: { nodes: allTeam = [] },
    allFishermanBusinessWebsitePage: { nodes: allPages = [] } = {},
    fishermanBusinessWebsitePage: { components: contentNodeComponents = [] } = {},
    allFishermanBusinessFile: { nodes: allFiles = [] },
    allFishermanBusinessMenuCategory: { nodes: allCategories = [] } = {},
    allFishermanBusinessMenuItem: { nodes: allItems = [] } = {},
  } = data;
  const {
    pageSlug,
    fishermanParentPageTitle = 'Locations',
    fishermanParentPageSlug = '/locations/',
    templateProps = {},
  } = pageContext;
  const {
    destinationEmail,
    destinationEmails,
    showEmail = true,
    teamMembersHeaderText = 'The Team Is Here For You',
    useBusinessNameInMapQuery = true,
    displayServiceSchedules = true,
    priceRangeProps = {},
    locationServicesGridProps = {},
  } = templateProps || {};

  const { name: businessName = '', website } = business || {};

  const url = getPageUrl(website, [
    createLocationSlug(location, allLocations, fishermanParentPageSlug),
  ]);

  const {
    _id: locationId,
    name,
    street,
    city,
    state,
    zipCode,
    phoneNumber,
    email,
    hours = [],
    featuredImage,
    locationImages = [],
    seoTitle,
    seoDescription,
    seoOgTitle,
    seoOgDescription,
    seoOgImage,
    interactions = [],
    description = '',
  } = location || {};

  function createPageDescription() {
    return `Discover our prime location in ${city}. Offering a range of services and amenities to cater to your needs. Visit us in ${city} today!`;
  }

  const locationTeamMembers = getLocationTeamMembers(allTeam, locationId);
  const locationName = createLocationName({ name, street, city });
  const hasContactInfo = phoneNumber || email;
  const teamPage = getPageByType(allPages, 'Team');
  const { title: teamPageTitle, slug: teamPageSlug } = teamPage || {};

  const locationBusinessFileIds = (locationImages || []).map(({ _id }) => _id);
  const locationBusinessFiles = getBusinessFiles(locationBusinessFileIds, allFiles);

  const locationItems = getRelatedServicesForLocation(location, allItems);

  const featuredBusinessFile = getBusinessFile(featuredImage, allFiles);
  const { src: featuredImageSrc, altText: featuredImageAltText } = featuredBusinessFile || {};

  const servicesHeaderContent = getComponentContentNodeContent({
    components: contentNodeComponents,
    componentId: 'location_services_header',
  });
  const servicesDescriptionContent = getComponentContentNodeContent({
    components: contentNodeComponents,
    componentId: 'location_services_description',
  });
  const seeAllTeamMembersButtonContent = getComponentContentNodeContent({
    components: contentNodeComponents,
    componentId: 'see_all_team_button',
  });

  return (
    <>
      <SEO
        businessName={businessName}
        pageName={`${locationName} Location`}
        title={seoTitle}
        description={seoDescription || createPageDescription()}
        images={[featuredImageSrc]}
        ogTitle={seoOgTitle}
        ogDescription={seoOgDescription}
        ogImage={seoOgImage}
        ogUrl={url}
      />
      <LocationSchema
        businessName={businessName}
        businessCategories={business.businessCategories}
        url={constructFullPageUrlFromSegments(baseUrl, fishermanParentPageSlug, pageSlug)}
        address={{
          streetAddress: street,
          addressLocality: city,
          addressRegion: state,
          postalCode: zipCode,
        }}
        telephone={phoneNumber}
      />
      <BreadcrumbListSchema
        items={[
          {
            name: `${fishermanParentPageTitle} | ${businessName}`,
            url: constructFullPageUrl(baseUrl, ensureSlugSlashes(fishermanParentPageSlug)),
          },
          {
            name: `${locationName} Location | ${businessName}`,
          },
        ]}
      />
      <div className="location-page-container">
        <Grid stackable className="location-header-container contact-info-grid">
          <Grid.Column width={16} textAlign="left" className="header-column">
            <Breadcrumb>
              <Breadcrumb.Section
                link
                to={ensureSlugSlashes(fishermanParentPageSlug)}
                as={InternalLink}
              >
                {fishermanParentPageTitle}
              </Breadcrumb.Section>
              <Breadcrumb.Divider icon="right chevron" />
              <Breadcrumb.Section>{locationName}</Breadcrumb.Section>
            </Breadcrumb>
            <FishermanHeader as="h1">
              <span className="highlight">{locationName}</span>
            </FishermanHeader>
          </Grid.Column>

          {!_isEmpty(description) && (
            <Grid.Row>
              <Grid.Column width={16} computer={8} className="description-column">
                <MarkdownContent content={description} />
              </Grid.Column>
            </Grid.Row>
          )}
          <Grid.Row>
            <Grid.Column width={16} className="ctas-container">
              {interactions.map((interaction) => {
                return (
                  <WebsiteComponentInteraction
                    key={interaction._id}
                    interaction={interaction}
                    className="location-interaction"
                    displayComponentPropsMap={{
                      button: {
                        size: null,
                        compact: false,
                      },
                    }}
                    event={{
                      category: EVENT_CATEGORIES.LOCATION_EVENT_CATEGORY,
                      action: EVENT_ACTIONS.LOCATION_INTERACTION_ACTION,
                    }}
                  />
                );
              })}
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            {!_isNil(featuredBusinessFile) && (
              <Grid.Column width={8} className="hero-image-column">
                <Image src={featuredImageSrc} altText={featuredImageAltText} loadStrategy="eager" />
              </Grid.Column>
            )}
            {!_isEmpty(locationBusinessFiles) && (
              <Grid.Column width={8} className="image-gallery-column">
                <PaginatedGrid
                  rows={2}
                  columns={2}
                  className=""
                  mobile={{ columns: 1, rows: 1, stackable: true }}
                  items={multipleComponentIterator({
                    iterator: locationBusinessFiles,
                    components: [
                      {
                        component: <SlideReveal direction="up" fade triggerOnce />,
                        children: [
                          {
                            component: <Image loadStrategy="eager" />,
                            propMap: { src: 'src', altText: 'altText' },
                          },
                        ],
                      },
                    ],
                  })}
                />
              </Grid.Column>
            )}
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={8} className="form-column">
              <ContactForm
                buttonText="Submit"
                centeredHeaders={false}
                hideOptIn={false}
                className=""
                fluidButton={false}
                inline={false}
                showLabels
                subheader=""
                termsCopy=""
                withContainer={false}
                locationId={locationId}
                businessId={business._id}
                businessType={business.type}
                destinationEmail={destinationEmail}
                destinationEmails={destinationEmails}
                event={{ category: 'Contact', action: 'Submit Inquiry' }}
              />
            </Grid.Column>
            <Grid.Column width={8} className="info-column">
              {hasContactInfo && (
                <div className="contact-info-container">
                  <FishermanHeader as="h2">Contact</FishermanHeader>
                  <div>
                    {email && <Email email={email} />}
                    {phoneNumber && <Phone phone={phoneNumber} />}
                  </div>
                </div>
              )}
              <div className="location-info-container">
                <FishermanHeader as="h2">Location</FishermanHeader>
                <div>
                  <Location
                    address={{
                      street,
                      city,
                      state,
                      zip: zipCode,
                    }}
                    email={showEmail ? email : null}
                    businessName={useBusinessNameInMapQuery ? businessName : ''}
                    showBusinessName={false}
                  />
                  <Map
                    address={{
                      street,
                      city,
                      state,
                      zip: zipCode,
                    }}
                    businessName={useBusinessNameInMapQuery ? businessName : ''}
                  />
                </div>
              </div>
              <div className="hours-info-container">
                <FishermanHeader as="h2">Hours</FishermanHeader>
                <div>
                  <HoursOfOperation hours={hours} displayOption="grouped" />
                </div>
              </div>
            </Grid.Column>
          </Grid.Row>

          {!_isEmpty(locationItems) && (
            <Grid.Column width={16} className="services-column">
              <div className="content-section">
                <FishermanHeader as="h2" content={servicesHeaderContent} />
                <MarkdownContent content={servicesDescriptionContent} />
              </div>

              <div className="services-section">
                <LocationServices
                  items={locationItems}
                  allItems={allItems}
                  allCategories={allCategories}
                  priceRangeProps={priceRangeProps}
                  displaySchedules={displayServiceSchedules}
                  itemGridProps={locationServicesGridProps}
                />
              </div>
            </Grid.Column>
          )}

          {!_isEmpty(locationTeamMembers) && (
            <Grid.Column width={16} className="featured-team-column">
              <div className="team-content-section">
                <FishermanHeader as="h2">{teamMembersHeaderText}</FishermanHeader>
                {teamPage && (
                  <Button
                    to={ensureSlugSlashes(teamPageSlug)}
                    primary
                    as={InternalLink}
                    event={{
                      category: EVENT_CATEGORIES.TEAM_EVENT_CATEGORY,
                      action: EVENT_ACTIONS.TEAM_VIEW_ALL_MEMBERS_EVENT_ACTION,
                      label: EVENT_LABELS.LOCATION_LABEL,
                    }}
                  >
                    {seeAllTeamMembersButtonContent || `See all ${teamPageTitle}`}
                  </Button>
                )}
              </div>
              <div className="team-members-section">
                <Grid className="featured-stylists-grid" columns={2} stackable>
                  <MultipleComponentIterator
                    iterator={locationTeamMembers}
                    limit={8}
                    components={[
                      {
                        component: (
                          <Grid.Column
                            as={InternalLink}
                            className="team-member"
                            event={{ category: 'Team', action: 'View Team Member' }}
                          />
                        ),
                        propMap: { key: '_id', to: createTeamMemberSlug, 'event.label': 'name' },
                        children: [
                          {
                            component: <HoverContainer />,
                            children: [
                              {
                                component: <div className="name-container" />,
                                children: [
                                  {
                                    component: <FishermanHeader />,
                                    propMap: { content: 'name' },
                                  },
                                  {
                                    component: (
                                      <HoverContainer.Content
                                        displayOnTouchScreen
                                        animation="height"
                                        animationProps={{ duration: 750, fade: true }}
                                      />
                                    ),
                                    children: [
                                      {
                                        component: <FishermanIcon iconName="arrow-right" />,
                                      },
                                    ],
                                  },
                                ],
                              },
                              {
                                component: (
                                  <HoverContainer.Content
                                    displayOnTouchScreen
                                    animation="height"
                                    animationProps={{ duration: 750, fade: true }}
                                  />
                                ),
                                children: [
                                  {
                                    component: <span className="role" />,
                                    propMap: { children: 'role' },
                                  },
                                ],
                              },
                              {
                                component: (
                                  <HoverContainer.Content
                                    displayOnTouchScreen={false}
                                    animation="height"
                                    animationProps={{ duration: 750, fade: true }}
                                  />
                                ),
                                children: [
                                  {
                                    component: <TeamMemberV2.Social />,
                                    propMap: { social: 'socialMedia' },
                                  },
                                ],
                              },
                            ],
                          },
                        ],
                      },
                    ]}
                  />
                </Grid>
              </div>
            </Grid.Column>
          )}
        </Grid>
      </div>
    </>
  );
}

export const query = graphql`
  query ($id: Int!) {
    site {
      siteMetadata {
        baseUrl
      }
    }
    fishermanBusiness {
      _id
      type
      name
      businessCategories {
        category
      }
      website {
        domains {
          url
          primaryDomain
        }
      }
    }
    allFishermanBusinessWebsitePage {
      nodes {
        url
        title
        pageType
        slug
      }
    }
    fishermanBusinessWebsitePage(pageType: { eq: "Locations" }) {
      components {
        componentIdentifier
        contentNodes {
          content
        }
      }
    }
    allFishermanBusinessLocation {
      nodes {
        _id
        name
        city
        street
      }
    }
    fishermanBusinessLocation(_id: { eq: $id }) {
      _id
      city
      country
      email
      hourLabels
      latitude
      longitude
      name
      order
      phoneNumber
      primaryLocation
      state
      street
      timezone
      uuid
      description
      verifiedAddress
      zipCode
      hours {
        close
        day
        label
        location
        open
        openAllDay
        _id
      }
      featuredImage
      locationImages {
        _id
      }
      seoTitle
      seoDescription
      seoOgTitle
      seoOgDescription
      seoOgImage {
        url
        alt
        type
      }
      interactions {
        _id
        displayType
        behaviorType
        url
        enabled
        style
        label
        role
      }
    }
    allFishermanBusinessTeam(filter: { show: { eq: true } }) {
      nodes {
        _id
        name
        role
        group
        email
        phone
        description
        image
        gatsbyImage {
          childImageSharp {
            gatsbyImageData(layout: FULL_WIDTH)
          }
        }
        socialMedia {
          platform
          url
        }
        hours {
          startDay
          openTime
          closeTime
        }
        locations {
          _id
          name
          location
          merchantLocation
        }
        show
      }
    }
    allFishermanBusinessFile {
      nodes {
        _id
        file
        gatsbyFile {
          childImageSharp {
            gatsbyImageData(layout: FULL_WIDTH)
          }
        }
        description
      }
    }
    allFishermanBusinessMenuCategory(sort: { order: ASC, fields: order }) {
      nodes {
        _id
        name
        items
      }
    }
    allFishermanBusinessMenuItem(sort: { order: ASC, fields: order }) {
      nodes {
        _id
        name
        visible
        category {
          _id
          schedule {
            _id
            name
          }
          name
        }
        locations {
          location
          displayOnLocationPage
        }
        variations {
          _id
          name
          order
          price
        }
      }
    }
  }
`;
